import { API, currentUser, sessionGeneration, setCurrentUser } from './state';
import type { User, RequestOptions } from './types';

export function saveSession(user: User) {
  setCurrentUser(user);
}

export function clearSession() {
  setCurrentUser(null);
  revalidatedReads.clear();
  try {
    localStorage.removeItem('zen_token');
    localStorage.removeItem('zen_refresh');
    localStorage.removeItem('zen_user');
  } catch (e) {}
}

// 旧 localStorage 認証の残骸を起動時に消去
try {
  localStorage.removeItem('zen_token');
  localStorage.removeItem('zen_refresh');
  localStorage.removeItem('zen_user');
} catch (e) {}

const RESTORE_RETRY_DELAYS_MS = [1000, 3000];

// Only 401/403 mean the sign-in is gone. A busy server (429) or a blip (5xx,
// network) is retried, so a momentary failure does not show the login screen.
export async function tryRestoreSession(retryDelaysMs: readonly number[] = RESTORE_RETRY_DELAYS_MS) {
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        saveSession(data.user);
        return true;
      }
      if (res.status === 401 || res.status === 403) break;
    } catch (e) {
      // Retried below like a 5xx.
    }
    if (attempt >= retryDelaysMs.length) break;
    await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
  }
  clearSession();
  return false;
}

// _logoutFn is set by app.js to break circular dependency (api -> logout -> api)
let _logoutFn = () => {};
export function setLogoutFn(fn: () => void) { _logoutFn = fn; }

// Requests sent together all come back 401 when the sign-in ends. They share
// one check of the session, so the login screen is shown once, not once per
// request (each sign-out clears what the person is typing on that screen).
let sessionRecheck: Promise<boolean> | null = null;
function recheckSessionAfter401(): Promise<boolean> {
  if (!sessionRecheck) {
    sessionRecheck = tryRestoreSession()
      .then((restored) => {
        if (!restored) _logoutFn();
        return restored;
      })
      .finally(() => { sessionRecheck = null; });
  }
  return sessionRecheck;
}

// The goal list is several MB and is read again every 30 seconds. Its last
// body is kept here with its ETag; when nothing changed the server answers
// 304 with no body and the kept body is used. Memory only, never the browser
// cache, so the list is still read from the server every time (no-store).
const revalidatedReads = new Map<string, { etag: string; text: string }>();

function isRevalidatedRead(path: string, method: string): boolean {
  return method === 'GET' && (path === '/api/goals' || path.startsWith('/api/goals?'));
}

export async function api(path: string, opts: RequestOptions = {}, _retried = false) {
  const generation = sessionGeneration;
  const headers: Record<string, string> = { 'X-Zenmetry-Client': 'web', ...(opts.headers || {}) };
  const revalidate = isRevalidatedRead(path, String(opts.method || 'GET').toUpperCase());
  const kept = revalidate ? revalidatedReads.get(path) : undefined;
  if (kept) headers['If-None-Match'] = kept.etag;
  const fetchBody: Record<string, unknown> = { ...opts };
  delete fetchBody.signIn;
  if (opts.json) {
    headers['Content-Type'] = 'application/json';
    (fetchBody as Record<string, unknown>).body = JSON.stringify(opts.json);
    delete (fetchBody as Record<string, unknown>).json;
  }
  const fetchOpts: RequestInit = { ...fetchBody, headers, credentials: 'include' as RequestCredentials };
  if (opts.signal) fetchOpts.signal = opts.signal;
  const res = await fetch(`${API}${path}`, fetchOpts);
  // Optional feature routes may be absent and Express can return an HTML 404.
  // Keep the HTTP status available to callers instead of failing while parsing
  // that response, so embedded settings surfaces can distinguish unavailable
  // (404) from authorization/conflict failures. Successful responses retain
  // the historical strict JSON parse: malformed 2xx responses must reject.
  let data: any;
  if (res.status === 304 && kept) {
    return JSON.parse(kept.text);
  }
  if (res.ok && revalidate) {
    const text = await res.text();
    data = JSON.parse(text);
    const etag = res.headers.get('ETag');
    if (etag) revalidatedReads.set(path, { etag, text });
    else revalidatedReads.delete(path);
  } else if (res.ok) {
    data = await res.json();
  } else {
    data = {};
    try {
      data = await res.json();
    } catch {
      // The status and generic HTTP message below are the safe fallback.
    }
  }

  // Auto-refresh on 401. A request sent for a sign-in that has already ended
  // (or with nobody signed in) leaves the login screen as it is.
  if (res.status === 401 && !_retried && !opts.signIn) {
    const refreshed = currentUser && generation === sessionGeneration
      ? await recheckSessionAfter401()
      : false;
    if (refreshed) {
      return api(path, opts, true);
    } else {
      throw new Error('セッションが切れました。再ログインしてください。');
    }
  }

  if (!res.ok) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`) as Error & {
      code?: string;
      outcome?: string;
      operationStatus?: string;
      judgment_packet?: unknown;
    };
    if (typeof data.code === 'string') err.code = data.code;
    if (typeof data.outcome === 'string') err.outcome = data.outcome;
    if (typeof data.status === 'string') err.operationStatus = data.status;
    if (data.judgment_packet && typeof data.judgment_packet === 'object') {
      err.judgment_packet = data.judgment_packet;
    }
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return data;
}

// In-flight GET dedupe: multiple concurrent callers to the same path share one HTTP request.
// Once the request resolves (success or failure), the entry clears so the next call refetches.
const _inFlightGets = new Map<string, Promise<unknown>>();

export function apiGetDedupe<T = unknown>(path: string): Promise<T> {
  const existing = _inFlightGets.get(path);
  if (existing) return existing as Promise<T>;
  const promise = api(path).finally(() => _inFlightGets.delete(path));
  _inFlightGets.set(path, promise);
  return promise as Promise<T>;
}
